import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Calendar,
  Plus,
  Pencil,
  Trash2,
  X,
  Check,
  Filter,
  RefreshCw,
  Clock,
  Tag,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  List,
  CalendarDays
} from 'lucide-react';
import {
  fetchAllHolidays,
  addHoliday,
  updateHoliday,
  deleteHoliday,
  clearError
} from '../../store/slices/holidaySlice';
import { PageHeader, Card, Badge, Table, Modal, FormField, LoadingState } from '../../components/common';
import { formatDate } from '../../utils/helpers';
import IsoDateInput from '../../components/common/Isodateinput'; // yyyy-mm-dd date input

const HOLIDAY_TYPES = ['Public', 'Company'];

const TYPE_STYLE = {
  Public: { code: 'PB', dot: 'bg-orange-400', chip: 'bg-orange-100 text-orange-700', ring: 'ring-orange-300' },
  // Bank:      { code: 'BK', dot: 'bg-blue-400',    chip: 'bg-blue-100 text-blue-700',      ring: 'ring-blue-300' },
  //School:    { code: 'SC', dot: 'bg-purple-400',  chip: 'bg-purple-100 text-purple-700',  ring: 'ring-purple-300' },
  //Religious: { code: 'RG', dot: 'bg-rose-400',    chip: 'bg-rose-100 text-rose-700',      ring: 'ring-rose-300' },
  //National:  { code: 'NT', dot: 'bg-teal-400',    chip: 'bg-teal-100 text-teal-700',      ring: 'ring-teal-300' },
  Company: { code: 'CM', dot: 'bg-indigo-400', chip: 'bg-indigo-100 text-indigo-700', ring: 'ring-indigo-300' },
  Other: { code: 'OT', dot: 'bg-surface-400', chip: 'bg-surface-100 text-surface-600', ring: 'ring-surface-300' },
};

const getTypeStyle = (type) => TYPE_STYLE[type] || TYPE_STYLE.Other;

const getStatusBadge = (isActive) => {
  return isActive === 'True' || isActive === true ?
    <Badge status="Active" className="bg-emerald-100 text-emerald-700" /> :
    <Badge status="Inactive" className="bg-surface-100 text-surface-500" />;
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

// ─── Helper Functions ───────────────────────────────────────────────────────

const toDateKey = (d) => {
  if (!d || isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const isSameDay = (a, b) => {
  if (!a || !b || isNaN(a.getTime()) || isNaN(b.getTime())) return false;
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
};

const buildMonthMatrix = (year, month) => {
  const firstOfMonth = new Date(year, month, 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7;
  const gridStart = new Date(year, month, 1 - startOffset);

  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
};

// ─── Helper: Parse Date ───────────────────────────────────────────────────────

const parseHolidayDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) {
      const year = parseInt(parts[0]);
      const month = parseInt(parts[1]) - 1;
      const day = parseInt(parts[2]);
      const date = new Date(year, month, day);
      if (!isNaN(date.getTime())) {
        return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
    }
    return null;
  } catch {
    return null;
  }
};

// ─── Check if holiday is active ─────────────────────────────────────────────

const isActiveHoliday = (holiday) => {
  return holiday.HAH_IS_ACTIVE === 'True' || holiday.HAH_IS_ACTIVE === true;
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function HolidayPage() {
  const dispatch = useDispatch();
  const { holidays, loading, addLoading, updateLoading, deleteLoading, error } =
    useSelector(state => state.holiday);

  // ── State ────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('calendar');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);
  const [formData, setFormData] = useState({
    holidayDate: '',
    holidayName: '',
    holidayType: '',
    isActive: true,
    remarks: ''
  });
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }, []);

  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDate, setSelectedDate] = useState(new Date(today.getFullYear(), today.getMonth(), today.getDate()));

  // ── Effects ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(fetchAllHolidays());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleAddSubmit = async (e) => {
    e.preventDefault();

    if (!formData.holidayDate) {
      alert('Please select a holiday date');
      return;
    }

    const result = await dispatch(addHoliday({
      holidayDate: formData.holidayDate,
      holidayName: formData.holidayName.trim(),
      holidayType: formData.holidayType,
      isActive: formData.isActive,
      createdBy: 'admin',
      remarks: formData.remarks.trim()
    }));

    if (result.type === 'holiday/add/fulfilled') {
      setShowAddModal(false);
      resetForm();

      await dispatch(fetchAllHolidays());

      if (formData.holidayDate) {
        const dateParts = formData.holidayDate.split('-');
        const newDate = new Date(
          parseInt(dateParts[0]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[2])
        );
        if (!isNaN(newDate.getTime())) {
          setSelectedDate(newDate);
          setViewDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
        }
      }
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const result = await dispatch(updateHoliday({
      holidayId: selectedHoliday?.HAH_HOLIDAY_ID,
      holidayName: formData.holidayName.trim(),
      holidayType: formData.holidayType,
      isActive: formData.isActive,
      remarks: formData.remarks.trim(),
      holidayDate: formData.holidayDate,
      updatedBy: 'admin'
    }));

    if (result.type === 'holiday/update/fulfilled') {
      setShowEditModal(false);
      resetForm();
      setSelectedHoliday(null);

      await dispatch(fetchAllHolidays());

      if (formData.holidayDate) {
        const dateParts = formData.holidayDate.split('-');
        const newDate = new Date(
          parseInt(dateParts[0]),
          parseInt(dateParts[1]) - 1,
          parseInt(dateParts[2])
        );
        if (!isNaN(newDate.getTime())) {
          setSelectedDate(newDate);
          setViewDate(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
        }
      }
    }
  };

  const handleDelete = async () => {
    if (selectedHoliday) {
      const result = await dispatch(deleteHoliday(selectedHoliday.HAH_HOLIDAY_ID));
      if (result.type === 'holiday/delete/fulfilled') {
        setShowDeleteConfirm(false);
        setSelectedHoliday(null);
        await dispatch(fetchAllHolidays());
      }
    }
  };

  const openEditModal = (holiday) => {
    setSelectedHoliday(holiday);

    let holidayDate = '';
    if (holiday.HAH_HOLIDAY_DATE) {
      const dateObj = new Date(holiday.HAH_HOLIDAY_DATE);
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        holidayDate = `${year}-${month}-${day}`;
      }
    }

    setFormData({
      holidayDate: holidayDate,
      holidayName: holiday.HAH_HOLIDAY_NAME || '',
      holidayType: holiday.HAH_HOLIDAY_TYPE || '',
      isActive: holiday.HAH_IS_ACTIVE === 'True' || holiday.HAH_IS_ACTIVE === true,
      remarks: holiday.HAH_REMARKS || ''
    });
    setShowEditModal(true);
  };

  const openDeleteConfirm = (holiday) => {
    setSelectedHoliday(holiday);
    setShowDeleteConfirm(true);
  };

  const resetForm = () => {
    setFormData({
      holidayDate: '',
      holidayName: '',
      holidayType: '',
      isActive: true,
      remarks: ''
    });
  };

  const handleRefresh = async () => {
    await dispatch(fetchAllHolidays());
  };

  // ── Filtering (table view) ──────────────────────────────────────────────────

  const filteredHolidays = holidays.filter(holiday => {
    const matchesType = filterType === 'all' || holiday.HAH_HOLIDAY_TYPE === filterType;
    const matchesSearch = holiday.HAH_HOLIDAY_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      holiday.HAH_HOLIDAY_TYPE?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesType && matchesSearch;
  });

  // ── Stats ────────────────────────────────────────────────────────────────────

  const totalHolidays = holidays.length;
  const activeHolidays = holidays.filter(h => isActiveHoliday(h)).length;
  const upcomingHolidays = holidays.filter(h => {
    const date = new Date(h.HAH_HOLIDAY_DATE);
    return date >= today && isActiveHoliday(h);
  }).length;

  // ── Calendar derived data ───────────────────────────────────────────────────

  const activeHolidaysList = useMemo(() => {
    return holidays.filter(h => isActiveHoliday(h));
  }, [holidays]);

  const holidaysByDate = useMemo(() => {
    const map = {};

    activeHolidaysList.forEach(h => {
      if (!h.HAH_HOLIDAY_DATE) return;

      const dateObj = new Date(h.HAH_HOLIDAY_DATE);
      if (isNaN(dateObj.getTime())) return;

      const key = toDateKey(dateObj);
      if (!key) return;

      if (!map[key]) map[key] = [];
      map[key].push(h);
    });
    return map;
  }, [activeHolidaysList]);

  const monthDays = useMemo(
    () => buildMonthMatrix(viewDate.getFullYear(), viewDate.getMonth()),
    [viewDate]
  );

  const holidaysThisMonth = useMemo(() => {
    return activeHolidaysList.filter(h => {
      if (!h.HAH_HOLIDAY_DATE) return false;
      const d = new Date(h.HAH_HOLIDAY_DATE);
      return d && !isNaN(d.getTime()) &&
        d.getFullYear() === viewDate.getFullYear() &&
        d.getMonth() === viewDate.getMonth();
    });
  }, [activeHolidaysList, viewDate]);

  const selectedDayHolidays = useMemo(() => {
    const key = toDateKey(selectedDate);
    return key ? (holidaysByDate[key] || []) : [];
  }, [holidaysByDate, selectedDate]);

  const isSelectedToday = isSameDay(selectedDate, today);

  const goPrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  const goNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

  const typesInUse = useMemo(() => {
    const set = new Set(activeHolidaysList.map(h => h.HAH_HOLIDAY_TYPE).filter(Boolean));
    return Array.from(set);
  }, [activeHolidaysList]);


  return (
    <div className="p-4">
      {/* ── Page Header ── */}
      <PageHeader
        title="Holiday Calendar"
        subtitle="Manage company holidays and public holidays"
        actions={
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefresh}
              className="btn-ghost flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={() => { resetForm(); setShowAddModal(true); }}
              className="flex shrink-0 items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700"
            > Add Holiday
            </button>
          </div>
        }
      />

      {/* ── Error Banner ── */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => dispatch(clearError())} className="ml-auto text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Total Holidays" value={totalHolidays} icon={Calendar} color="blue" />
        <StatCard label="Active Holidays" value={activeHolidays} icon={Check} color="green" />
        <StatCard label="Upcoming" value={upcomingHolidays} icon={Clock} color="gold" />
        <StatCard label="Holiday Types" value={new Set(holidays.map(h => h.HAH_HOLIDAY_TYPE)).size} icon={Tag} color="purple" />
      </div>

      {/* ── Tabs ── */}
      <div className="flex items-center gap-2 mb-5 border-b border-surface-200">
        <button
          onClick={() => setActiveTab('calendar')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'calendar'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-surface-500 hover:text-surface-700'
            }`}
        >
          <CalendarDays size={16} /> Calendar View
        </button>
        <button
          onClick={() => setActiveTab('table')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === 'table'
              ? 'border-primary-600 text-primary-700'
              : 'border-transparent text-surface-500 hover:text-surface-700'
            }`}
        >
          <List size={16} /> Table View
        </button>
      </div>

      {/* ── CALENDAR TAB ── */}
      {activeTab === 'calendar' && (
        loading ? (
          <Card><LoadingState /></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2">
              <Card noPad className="overflow-hidden">
                {/* Month header */}
                <div className="bg-gradient-to-br from-primary-700 to-primary-500 text-white px-6 pt-5 pb-8 rounded-t-xl">
                  <div className="flex items-center justify-between mb-1">
                    <button
                      onClick={goPrevMonth}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <div className="text-center">
                      <div className="text-xs tracking-widest opacity-80">{viewDate.getFullYear()}</div>
                      <div className="text-2xl font-bold tracking-wide">{MONTH_NAMES[viewDate.getMonth()].toUpperCase()}</div>
                    </div>
                    <button
                      onClick={goNextMonth}
                      className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </div>
                  <div className="grid grid-cols-7 mt-4 text-xs font-semibold tracking-wide">
                    {WEEKDAY_LABELS.map((wd, i) => (
                      <div key={wd} className={`text-center ${i >= 5 ? 'text-white' : 'text-white/80'}`}>
                        {wd}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Day grid */}
                {/* Day grid */}
                <div className="grid grid-cols-7 gap-y-1 px-3 pb-4 -mt-4 bg-white rounded-t-2xl relative">
                  {monthDays.map((d, idx) => {
                    const inMonth = d.getMonth() === viewDate.getMonth();
                    const key = toDateKey(d);
                    const dayHolidays = key ? (holidaysByDate[key] || []) : [];
                    const isSunday = d.getDay() === 0;
                    const isSaturday = d.getDay() === 6;
                    const isToday = isSameDay(d, today);
                    const isSelected = isSameDay(d, selectedDate);
                    const primaryType = dayHolidays[0]?.HAH_HOLIDAY_TYPE;
                    const style = primaryType ? getTypeStyle(primaryType) : null;

                    // Base text color per day type
                    let textColor = 'text-surface-800';
                    if (!inMonth) textColor = 'text-surface-300';
                    else if (isSunday) textColor = 'text-red-500';
                    else if (isSaturday) textColor = 'text-surface-600';

                    return (
                      <button
                        key={idx}
                        onClick={() => setSelectedDate(new Date(d))}
                        className="flex items-center justify-center py-2"
                      >
                        <span
                          className={`relative w-9 h-9 flex items-center justify-center rounded-full text-sm transition-colors
            ${textColor}
            ${isToday ? 'bg-primary-700 text-white font-semibold' : ''}
            ${!isToday && dayHolidays.length > 0 && inMonth ? style?.chip || 'bg-surface-100 text-surface-700' : ''}
            ${!isToday && (isSunday || isSaturday) && inMonth && dayHolidays.length === 0 ? 'bg-surface-100' : ''}
            ${isSelected && !isToday ? `ring-2 ${style ? style.ring : 'ring-primary-300'}` : ''}
            ${!isToday && dayHolidays.length > 0 && inMonth ? 'font-semibold' : ''}
          `}
                        >
                          {d.getDate()}
                          {dayHolidays.length > 1 && (
                            <span className="absolute -bottom-1 w-1.5 h-1.5 rounded-full bg-primary-500" />
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </Card>

              {/* Legend - Show only active holiday types */}
              <div className="flex flex-wrap items-center gap-2 mt-4">
                {typesInUse.length > 0 ? (
                  typesInUse.map(type => {
                    const style = getTypeStyle(type);
                    return (
                      <span
                        key={type}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${style.chip}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                        {style.code} {type}
                      </span>
                    );
                  })
                ) : (
                  <span className="text-sm text-surface-400">No active holidays</span>
                )}
              </div>
            </div>

            {/* Side panel: selected day details */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-surface-800">
                  {isSelectedToday ? 'Today' : formatDate ? formatDate(selectedDate) : selectedDate.toLocaleDateString()}
                </h3>
                <span className="text-sm text-primary-600 font-medium">
                  {holidaysThisMonth.length} active holiday{holidaysThisMonth.length !== 1 ? 's' : ''} this month
                </span>
              </div>

              {selectedDayHolidays.length === 0 ? (
                <div className="p-5 border border-dashed border-surface-300 rounded-xl text-center text-surface-400 text-sm">
                  No active holiday on this date — regular working day.
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDayHolidays.map(h => {
                    const style = getTypeStyle(h.HAH_HOLIDAY_TYPE);
                    return (
                      <Card key={h.HAH_HOLIDAY_ID} className="flex items-start gap-3">
                        <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold ${style.chip}`}>
                          {selectedDate.getDate()}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-surface-800 truncate">{h.HAH_HOLIDAY_NAME}</p>
                          <p className="text-sm text-surface-400">{h.HAH_HOLIDAY_TYPE}</p>
                          {h.HAH_REMARKS && (
                            <p className="text-xs text-surface-400 mt-1">{h.HAH_REMARKS}</p>
                          )}
                        </div>
                        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-bold ${style.chip}`}>
                          {style.code}
                        </span>
                      </Card>
                    );
                  })}
                </div>
              )}

              {holidaysThisMonth.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs font-semibold text-surface-400 uppercase tracking-wide mb-2">
                    All active holidays this month
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {holidaysThisMonth
                      .slice()
                      .sort((a, b) => {
                        const dateA = new Date(a.HAH_HOLIDAY_DATE);
                        const dateB = new Date(b.HAH_HOLIDAY_DATE);
                        return dateA - dateB;
                      })
                      .map(h => {
                        const style = getTypeStyle(h.HAH_HOLIDAY_TYPE);
                        const d = new Date(h.HAH_HOLIDAY_DATE);
                        return (
                          <button
                            key={h.HAH_HOLIDAY_ID}
                            onClick={() => setSelectedDate(new Date(d))}
                            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-surface-50 text-left transition-colors"
                          >
                            <span className={`w-2 h-2 rounded-full ${style.dot}`} />
                            <span className="text-sm font-medium text-surface-700 w-6">{d.getDate()}</span>
                            <span className="text-sm text-surface-600 truncate">{h.HAH_HOLIDAY_NAME}</span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      )}

      {/* ── TABLE TAB ── */}
      {activeTab === 'table' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Filter size={16} className="text-surface-400" />
              <span className="text-sm font-medium text-surface-600">Filter:</span>
            </div>
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filterType === 'all'
                  ? 'bg-primary-100 text-primary-700 font-medium'
                  : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                }`}
            >
              All
            </button>
            {HOLIDAY_TYPES.map(type => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors ${filterType === type
                    ? 'bg-primary-100 text-primary-700 font-medium'
                    : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
                  }`}
              >
                {type}
              </button>
            ))}
            <div className="flex-1" />
            <input
              type="text"
              placeholder="Search holidays..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field w-full sm:w-64 text-sm"
            />
          </div>

          {/* Table */}
          <Card noPad>
            <div className="p-4 border-b border-surface-100 flex items-center justify-between">
              <h3 className="font-semibold text-surface-800">Holiday List</h3>
              <span className="text-sm text-surface-400">{filteredHolidays.length} entries</span>
            </div>

            {loading ? (
              <LoadingState />
            ) : (
              <Table headers={['Date', 'Holiday Name', 'Type', 'Status', 'Remarks', 'Actions']}>
                {filteredHolidays.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-4 py-8 text-center text-surface-400">
                      <div className="flex flex-col items-center gap-2">
                        <Calendar size={32} className="text-surface-200" />
                        <span>No holidays found</span>
                        <button
                          onClick={() => { resetForm(); setShowAddModal(true); }}
                          className="text-primary-600 text-sm hover:underline"
                        >
                          Add your first holiday
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredHolidays.map((holiday) => {
                    let displayDate = holiday.HAH_HOLIDAY_DATE;
                    try {
                      const dateObj = new Date(holiday.HAH_HOLIDAY_DATE);
                      if (!isNaN(dateObj.getTime())) {
                        displayDate = formatDate ? formatDate(dateObj) : dateObj.toLocaleDateString();
                      }
                    } catch {
                      displayDate = holiday.HAH_HOLIDAY_DATE;
                    }

                    return (
                      <tr key={holiday.HAH_HOLIDAY_ID} className="table-row">
                        <td className="px-4 py-3 text-surface-600 text-sm">
                          {displayDate}
                        </td>
                        <td className="px-4 py-3 font-medium text-surface-800">
                          {holiday.HAH_HOLIDAY_NAME}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${getTypeStyle(holiday.HAH_HOLIDAY_TYPE).chip}`}>
                            {holiday.HAH_HOLIDAY_TYPE || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(holiday.HAH_IS_ACTIVE)}
                        </td>
                        <td className="px-4 py-3 text-surface-500 text-sm max-w-xs truncate">
                          {holiday.HAH_REMARKS || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openEditModal(holiday)}
                              className="p-1.5 text-surface-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            {/* <button
                              onClick={() => openDeleteConfirm(holiday)}
                              className="p-1.5 text-surface-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={15} />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </Table>
            )}
          </Card>
        </>
      )}

      {/* ── Add Modal ── */}
      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Holiday"
        maxWidth="md"
      >
        <form onSubmit={handleAddSubmit} className="space-y-5">
          <FormField label="Holiday Date" required>
            {/* OLD native date input (kept for reference):
            <input
              type="date"
              className="input-field"
              value={formData.holidayDate}
              onChange={(e) => setFormData({ ...formData, holidayDate: e.target.value })}
              required
            />
            */}
            <IsoDateInput
              value={formData.holidayDate}
              onChange={(value) => setFormData({ ...formData, holidayDate: value })}
              className="input-field"
            />
          </FormField>

          <FormField label="Holiday Name" required>
            <input
              type="text"
              className="input-field"
              placeholder="e.g. Independence Day"
              value={formData.holidayName}
              onChange={(e) => setFormData({ ...formData, holidayName: e.target.value })}
              required
            />
          </FormField>

          <FormField label="Holiday Type" required>
            <select
              className="input-field"
              value={formData.holidayType}
              onChange={(e) => setFormData({ ...formData, holidayType: e.target.value })}
              required
            >
              <option value="">Select type...</option>
              {HOLIDAY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Status">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded border-surface-300 focus:ring-primary-500"
              />
              <span className="text-sm text-surface-600">Active</span>
            </label>
          </FormField>

          <FormField label="Remarks">
            <textarea
              className="input-field"
              rows="2"
              placeholder="Additional notes..."
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </FormField>

          <div className="flex justify-end gap-3 pt-2 border-t border-surface-100">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="btn-ghost"
              disabled={addLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={addLoading}
            >
              {addLoading ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  Adding...
                </>
              ) : (
                'Add Holiday'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Edit Modal ── */}
      <Modal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setSelectedHoliday(null); }}
        title="Edit Holiday"
        maxWidth="md"
      >
        <form onSubmit={handleEditSubmit} className="space-y-5">
          <div className="bg-surface-50 p-3 rounded-lg text-sm text-surface-500 flex items-center gap-2">
            <Calendar size={16} />
            <span>Editing: <span className="font-medium text-surface-700">{selectedHoliday?.HAH_HOLIDAY_NAME}</span></span>
            <span className="ml-auto text-xs">
              ID: {selectedHoliday?.HAH_HOLIDAY_ID}
            </span>
          </div>

          <FormField label="Holiday Date" required>
            {/* OLD native date input (kept for reference):
            <input
              type="date"
              className="input-field"
              value={formData.holidayDate}
              onChange={(e) => setFormData({ ...formData, holidayDate: e.target.value })}
              required
            />
            */}
            <IsoDateInput
              value={formData.holidayDate}
              onChange={(value) => setFormData({ ...formData, holidayDate: value })}
              className="input-field"
            />
          </FormField>

          <FormField label="Holiday Name" required>
            <input
              type="text"
              className="input-field"
              placeholder="Holiday name"
              value={formData.holidayName}
              onChange={(e) => setFormData({ ...formData, holidayName: e.target.value })}
              required
            />
          </FormField>

          <FormField label="Holiday Type">
            <select
              className="input-field"
              value={formData.holidayType}
              onChange={(e) => setFormData({ ...formData, holidayType: e.target.value })}
            >
              <option value="">Select type...</option>
              {HOLIDAY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </FormField>

          <FormField label="Status">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 text-primary-600 rounded border-surface-300 focus:ring-primary-500"
              />
              <span className="text-sm text-surface-600">Active</span>
            </label>
          </FormField>

          <FormField label="Remarks">
            <textarea
              className="input-field"
              rows="2"
              placeholder="Additional notes..."
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            />
          </FormField>

          <div className="flex justify-end gap-3 pt-2 border-t border-surface-100">
            <button
              type="button"
              onClick={() => { setShowEditModal(false); setSelectedHoliday(null); }}
              className="btn-ghost"
              disabled={updateLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex items-center gap-2"
              disabled={updateLoading}
            >
              {updateLoading ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Holiday'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* ── Delete Confirmation ── */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSelectedHoliday(null); }}
        title="Delete Holiday"
        maxWidth="sm"
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 p-4 bg-red-50 rounded-lg border border-red-200">
            <div className="p-2 bg-red-100 rounded-full text-red-600">
              <Trash2 size={24} />
            </div>
            <div>
              <p className="font-medium text-surface-800">
                Are you sure you want to delete this holiday?
              </p>
              <p className="text-sm text-surface-500 mt-1">
                <strong>{selectedHoliday?.HAH_HOLIDAY_NAME}</strong> on {
                  (() => {
                    try {
                      const dateObj = new Date(selectedHoliday?.HAH_HOLIDAY_DATE);
                      if (!isNaN(dateObj.getTime())) {
                        return formatDate ? formatDate(dateObj) : dateObj.toLocaleDateString();
                      }
                      return selectedHoliday?.HAH_HOLIDAY_DATE;
                    } catch {
                      return selectedHoliday?.HAH_HOLIDAY_DATE;
                    }
                  })()
                }
              </p>
            </div>
          </div>
          <p className="text-sm text-red-600 flex items-center gap-1">
            <AlertCircle size={14} />
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-3 pt-2 border-t border-surface-100">
            <button
              type="button"
              onClick={() => { setShowDeleteConfirm(false); setSelectedHoliday(null); }}
              className="btn-ghost"
              disabled={deleteLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <>
                  <RefreshCw size={15} className="animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 size={15} />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── StatCard Component ──────────────────────────────────────────────────────

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    gold: 'bg-amber-50 text-amber-600 border-amber-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    red: 'bg-red-50 text-red-600 border-red-100',
  };

  return (
    <div className={`p-4 rounded-xl border ${colorClasses[color] || colorClasses.blue}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-2xl font-bold mt-0.5">{value}</p>
        </div>
        {Icon && <Icon size={28} className="opacity-60" />}
      </div>
    </div>
  );
};